import { useState } from 'react';

import {
  WAuthLayout,
  WInputField,
  WButton,
  WTranslationProvider,
} from '@wareclouds/wareclouds-ui';
import { useYupValidationTranslations } from '../../../../common/hooks';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Typography, Box } from '@mui/material';

import logo from '../../../../../assets/img/logo.svg';
import {
  createAccountSchema,
  defaultValuesCreateAccountForm,
} from '../../utils';
import { useNavigate } from 'react-router-dom';
import EmailSentModal from '../components/EmailSentModal';
import LeadAccountExistsModal from '../components/LeadAccountExistsModal';
import { useCreateLead } from '../../hooks';

export default function CreateAccountPage() {
  const { t } = useTranslation(['onboarding', 'auth']);
  const translateError = useYupValidationTranslations();
  const translateFieldError = (error) => {
    if (error?.type === 'server') {
      return t(error.message, { ns: 'onboarding' });
    }
    const message = error?.message;
    if (typeof message === 'string' && message.includes(':')) {
      return t(message);
    }
    return translateError(error);
  };
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [leadExistsModalOpen, setLeadExistsModalOpen] = useState(false);
  const createLead = useCreateLead();

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    formState: { isSubmitting, isValid },
  } = useForm({
    resolver: yupResolver(createAccountSchema),
    defaultValues: defaultValuesCreateAccountForm,
    mode: 'onChange',
  });

  const onSubmit = async (data) => {
    const result = await createLead.submit({ email: data.email.trim() });

    if (result?.magicLinkSent) {
      setIsModalOpen(true);
      return;
    }

    if (result?.leadAlreadyCompleted || createLead.isLeadAlreadyCompleted) {
      setLeadExistsModalOpen(true);
      return;
    }

    if (result?.leadEmailExists) {
      setError('email', {
        type: 'server',
        message: result.serverErrorMessage,
      });
      return;
    }

    if (result?.internalServerError || createLead.isInternalServerError) {
      setError('email', {
        type: 'server',
        message:
          result?.serverErrorMessage ??
          createLead.serverErrorMessage ??
          'create_account.errors.internal_server_error',
      });
    }
  };

  const isSendingLead = isSubmitting || createLead.isPending;

  return (
    <WTranslationProvider t={t} translateError={translateError}>
      <WAuthLayout
        title={t('create_account.title', { ns: 'onboarding' })}
        logo={logo}
        termsAndConditionUrl="/terms"
        showTermsAndCondition
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <WInputField
            name="email"
            control={control}
            translateError={translateFieldError}
            textFieldProps={{
              label: t('create_account.form.email.label', { ns: 'onboarding' }),
              placeholder: t('create_account.form.email.placeholder', {
                ns: 'onboarding',
              }),
              fullWidth: true,
              margin: 'normal',
              disabled: isSendingLead,
              InputLabelProps: { shrink: true },
              type: 'email',
              autoComplete: 'email',
              onChange: (e) => {
                clearErrors('email');
                setValue('email', e.target.value, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              },
            }}
          />

          <WButton
            type="submit"
            label={t('create_account.button.create_account', {
              ns: 'onboarding',
            })}
            variant="contained"
            color="primary"
            fullWidth
            isLoading={isSendingLead}
            isDisabled={!isValid || isSendingLead}
            sx={{ mt: 3, mb: 2, py: 1.5, textTransform: 'uppercase' }}
          />

          <Box
            sx={{
              textAlign: 'center',
              mt: 2,
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: 16 }}
            >
              {t('create_account.links.already_have_account', {
                ns: 'onboarding',
              })}{' '}
              <WButton
                variant="text"
                label={t('create_account.links.login', { ns: 'onboarding' })}
                onClick={() => navigate('/login')}
                sx={{
                  textTransform: 'none',
                  p: 0,
                  minWidth: 'auto',
                  fontSize: 'inherit',
                  color: 'primary.main',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              />
            </Typography>
          </Box>
        </form>
      </WAuthLayout>

      <EmailSentModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
      <LeadAccountExistsModal open={leadExistsModalOpen} />
    </WTranslationProvider>
  );
}
